import {
  CalloutPageComponent,
  CollectionPageComponent,
  BookstoresPageComponent,
  PageComponent,
  TagsPageComponent,
  PageComponentData,
  TextPageComponent,
  TypesenseSearchProps,
  AlgoliaOptions,
  ImagePageComponent,
  FullTitlePageComponent,
  HtmlCssPageComponent,
  ContentPageComponent,
  SeriesPageComponent,
} from 'types';

import PageComponentCallout from './callout';
import Collection from './collection';
import Tags from './tags';
import Bookstores from './bookstores';

import styles from '../../styles/components/page-components/index.module.scss';
import { useGlobalState } from '../../state';
import classNames from 'classnames';
import TextContent from './text-content';
import { UserProfile } from 'firebase/auth';
import { algoliaToTypesenseOptions } from 'sdk';
import { Typesense } from 'sdk/src/clients/typesense/react-query';
import ImageComponent from './image';
import FullTitle from './full-title';
import HtmlCssComponent from './html-css';
import ContentComponent from './content';
import { fetchCollectionDocByPath } from '~/clients/firebase/client';
import { useQuery } from '@tanstack/react-query';
import { PageWithPath } from 'types';
import SeriesComponent from './series';
import TileRow from '../tile-row';
import ClientSeriesTile from '../tiles/client-series';
import { mapQueryBy, mapSortBy, search as tsSearch } from 'sdk';

interface PageComponentManagerProps {
  components: PageComponent<PageComponentData>[];
}

function CustomBookstoreHandler({
  component,
}: {
  component: PageComponent<PageComponentData>;
}) {
  const IS_CUSTOM_QUERY = Boolean(
    (component as PageComponent<BookstoresPageComponent>).data?.customOptions
  );
  let typesenseOptions;
  let customSellers;

  if (IS_CUSTOM_QUERY) {
    const customOptionsString = (
      component as PageComponent<BookstoresPageComponent>
    ).data?.customOptions;
    const unknownOptions = customOptionsString
      ? JSON.parse(customOptionsString)
      : {};
    if (unknownOptions?.params) {
      // these are typesense options
      typesenseOptions = unknownOptions as TypesenseSearchProps;
    } else {
      typesenseOptions = algoliaToTypesenseOptions({
        algoliaOptions: {
          index: 'users',
          options: unknownOptions as AlgoliaOptions['options'],
        },
      });
    }

    customSellers = Typesense<UserProfile>().search({
      props: typesenseOptions,
      reactQueryOptions: {
        enabled: Boolean(typesenseOptions?.index),
      },
    });
  }

  if (
    IS_CUSTOM_QUERY &&
    customSellers?.isFetched &&
    !customSellers?.data?.hits?.length
  ) {
    return null;
  }

  return (
    <Bookstores
      {...({
        ...component,
        data: {
          ...component.data,
          sellers: customSellers?.data?.hits?.length
            ? customSellers.data.hits
                .slice(0, 5)
                .map((s) => {
                  return { sellerId: s.document.id };
                })
                .filter(Boolean)
            : (component as PageComponent<BookstoresPageComponent>).data
                .sellers,
        },
      } as PageComponent<BookstoresPageComponent>)}
    />
  );
}

export default function PageComponents(props: PageComponentManagerProps) {
  const { state } = useGlobalState();
  const components = state.ui.data.adminPageEditing
    ? props.components
    : props.components.filter(
        (component) => component.publish && component.display.web
      );

  function getPageComponent() {
    return components.map((component) => {
      switch (component.type) {
        case 'collection':
          return (
            <div
              className={classNames(styles.component, styles[component.type])}
              key={component.id}
            >
              <Collection
                {...(component as PageComponent<CollectionPageComponent>)}
              />
            </div>
          );
        case 'tags':
          return (
            <div
              className={classNames(styles.component, styles[component.type])}
              key={component.id}
            >
              <Tags {...(component as PageComponent<TagsPageComponent>)} />
            </div>
          );
        case 'callout':
          return (
            <div
              className={classNames(styles.component, styles[component.type])}
              key={component.id}
            >
              <PageComponentCallout
                {...(component as PageComponent<CalloutPageComponent>)}
              />
            </div>
          );
        case 'bookstores':
          return (
            <div
              className={classNames(styles.component, styles[component.type])}
              key={component.id}
            >
              <CustomBookstoreHandler component={component} />
            </div>
          );
        case 'text':
          return (
            <div
              className={classNames(styles.component, styles[component.type])}
              key={component.id}
            >
              <div
                className={
                  styles[
                    `col-${(component.data as TextPageComponent)?.cols || '4'}`
                  ]
                }
              >
                <TextContent
                  mainComponent={component as PageComponent<TextPageComponent>}
                  colData={
                    component.data as TextPageComponent & { index?: number }
                  }
                />
              </div>
              {(component.data as TextPageComponent)?.additionalText?.map(
                (additionalText, index) => (
                  <div
                    key={index}
                    className={styles[`col-${additionalText?.cols || '1'}`]}
                  >
                    <TextContent
                      mainComponent={
                        component as PageComponent<TextPageComponent>
                      }
                      colData={
                        additionalText as TextPageComponent & { index?: number }
                      }
                    />
                  </div>
                )
              )}
            </div>
          );
        case 'image':
          return (
            <div
              className={classNames(styles.component, styles[component.type])}
              key={component.id}
            >
              <ImageComponent
                {...(component as PageComponent<ImagePageComponent>)}
              />
            </div>
          );
        case 'fullTitle':
          return (
            <div
              className={classNames(styles.component, styles[component.type])}
              key={component.id}
            >
              <FullTitle
                {...(component as PageComponent<FullTitlePageComponent>)}
              />
            </div>
          );
        case 'htmlCss':
          return (
            <div
              className={classNames(styles.component, styles[component.type])}
              key={component.id}
            >
              <HtmlCssComponent
                {...(component as PageComponent<HtmlCssPageComponent>)}
              />
            </div>
          );
        case 'content':
          return (
            <div
              className={classNames(styles.component, styles[component.type])}
              key={component.id}
            >
              <ContentLoader
                component={component as PageComponent<ContentPageComponent>}
              />
            </div>
          );
        case 'series':
          return (
            <div
              className={classNames(styles.component, styles[component.type])}
              key={component.id}
            >
              <SeriesLoader
                component={component as PageComponent<SeriesPageComponent>}
              />
            </div>
          );
      }
    });
  }

  return <section className={styles.container}>{getPageComponent()}</section>;
}

/**
 * Loader component responsible for fetching pages for the Related Content row
 * and outputting the ContentComponent once data is available.
 * Adds verbose console logging so we can debug path → firestore look-ups.
 */
function ContentLoader({
  component,
}: {
  component: PageComponent<ContentPageComponent>;
}) {
  // join paths for cache key uniqueness
  const pagePathsKey = component.data.page_paths?.join(',') || 'empty';

  const query = useQuery(['related-content-pages', pagePathsKey], async () => {
    const pagesPromises = component.data.page_paths.map(async (webPath) => {
      // ensure leading slash removed for calculation
      const trimmed = webPath.replace(/^\/+/, '').replace(/\/+$/, '');

      const segments = trimmed.split('/');
      const docId = segments.pop();
      let collectionPath = 'pages';
      if (segments.length) {
        collectionPath += '/' + segments.join('/pages/') + '/pages';
      }


      try {
        const page = await fetchCollectionDocByPath<PageWithPath>(
          docId,
          collectionPath,
          true
        );

        if (page) {
          // attach path so downstream components can use it
          return { ...page, path: webPath } as PageWithPath;
        }

        return null;
      } catch (err) {
        return null;
      }
    });

    const pages = await Promise.all(pagesPromises);
    // filter nulls
    return pages.filter(Boolean);
  });

  if (query.isLoading) return null;

  if (query.error) {
    return null;
  }

  console.log('[RelatedContent] Fetched pages', query.data?.length);

  if (!query.data?.length) return null;

  return (
    <ContentComponent
      pages={query.data as unknown as PageWithPath[]}
      title={component.data.title || 'Related content'}
      key={component.id}
    />
  );
}

/**
 * Loader for Series row – supports multiple series_ids.
 */
function SeriesLoader({
  component,
}: {
  component: PageComponent<SeriesPageComponent>;
}) {
  // Support both legacy titleId & new series_ids array for backwards compatibility
  const legacyTitleId = (component.data as any).titleId;
  const seriesIds: string[] = (component.data as any).series_ids || [];

  const queryKey = legacyTitleId
    ? ['series-for-title', legacyTitleId]
    : ['series-by-ids', seriesIds.join(',')];

  const query = useQuery(queryKey, async () => {
    if (legacyTitleId) {
      const { getBookSeries } = await import(
        '@/lib/data-fetching/get-book-series'
      );

      const { series } = await getBookSeries({ titleId: legacyTitleId });
      return series ? [series] : [];
    }

    if (!seriesIds?.length) return [];

    const { hits } = await tsSearch({
      index: 'series',
      params: {
        q: '*',
        query_by: mapQueryBy('series'),
        sort_by: mapSortBy('series'),
        filter_by: `id:[${seriesIds.join(',')}] && status:=live`,
        per_page: seriesIds.length,
      },
      options: {},
    });

    return hits?.map((h) => h.document) || [];
  });

  if (query.isLoading) return null;

  const seriesArr: any[] = query.data || [];
  if (!seriesArr.length) return null;

  return (
    <TileRow title={component.data.title} tileStyle="content" hideScrollbar>
      {seriesArr.map((s) => (
        <ClientSeriesTile series={s} key={s.id} />
      ))}
    </TileRow>
  );
}
